import React, { useState, useEffect } from 'react';
import { LessonAdminPanel } from './LessonAdminPanel';
import { WordBank } from './WordBank';
import { AdminUsers } from './AdminUsers';
import { PremiumUsers } from './PremiumUsers';
import { DonationLinksManager } from './DonationLinksManager';
import { CustomLessonRequests } from './CustomLessonRequests';
import { IssueReports } from './IssueReports';
import { LanguageSelection } from '../LanguageSelection';
import { LevelSelection } from '../LevelSelection';
import { AccountInfo } from '../AccountInfo';
import { Settings } from '../Settings';
import { type UserProfile } from '../../types';
import { type LanguageLevel } from '../../types';
import { supabase } from '../../utils/supabase';

export function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'words' | 'language' | 'level' | 'account' | 'settings' | 'admins' | 'donations' | 'requests' | 'issues' | 'premium'>('lessons');
  const [adminLanguage, setAdminLanguage] = useState<string>(() => {
    const storedLanguage = localStorage.getItem('adminLanguage');
    if (!storedLanguage) {
      localStorage.setItem('adminLanguage', 'English');
      return 'English';
    }
    return storedLanguage;
  });
  const [activeLevel, setActiveLevel] = useState<LanguageLevel>('A1');
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingIssuesCount, setPendingIssuesCount] = useState(0);

  useEffect(() => {
    console.log('Setting adminLanguage in localStorage:', adminLanguage);
    localStorage.setItem('adminLanguage', adminLanguage);
  }, [adminLanguage]);

  useEffect(() => {
    const loadPendingCounts = async () => {
      // Load pending requests count
      const { count: requestsCount } = await supabase
        .from('custom_lesson_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (requestsCount !== null) {
        setPendingRequestsCount(requestsCount);
      }

      // Load pending issues count
      const { count: issuesCount } = await supabase
        .from('lesson_issue_reports')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (issuesCount !== null) {
        setPendingIssuesCount(issuesCount);
      }
    };

    loadPendingCounts();
    const interval = setInterval(loadPendingCounts, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Content Language
          </label>
          <select
            value={adminLanguage}
            onChange={(e) => {
              const newLanguage = e.target.value;
              console.log('Language selection changed to:', newLanguage);
              setAdminLanguage(newLanguage);
            }}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="English">English</option>
            <option value="Persian">Persian</option>
          </select>
        </div>

        <div className="mb-8">
          <div className="sm:hidden">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as typeof activeTab)}
              className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="lessons">Lessons</option>
              <option value="words">Word Bank</option>
              <option value="language">Language</option>
              <option value="level">Level</option>
              <option value="account">Account</option>
              <option value="settings">Settings</option>
            </select>
          </div>
          <div className="hidden sm:block">
            <nav className="flex space-x-4">
              {[
                { id: 'lessons', label: 'Lessons' },
                { id: 'requests', label: 'Custom Requests', badge: pendingRequestsCount },
                { id: 'issues', label: 'Issue Reports', badge: pendingIssuesCount },
                { id: 'words', label: 'Word Bank' },
                { id: 'language', label: 'Language' },
                { id: 'level', label: 'Level' },
                { id: 'account', label: 'Account' },
                { id: 'settings', label: 'Settings' },
                { id: 'admins', label: 'Admin Users' },
                { id: 'premium', label: 'Premium Users' },
                { id: 'donations', label: 'Donation Links' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`px-3 py-2 rounded-md relative ${
                    activeTab === tab.id
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="mt-6">
          {activeTab === 'lessons' && (
            <div className="space-y-6">
              <div className="flex space-x-2">
                {['A1', 'A2', 'B1', 'B2', 'C1'].map((level) => (
                  <button
                    key={level}
                    onClick={() => setActiveLevel(level as LanguageLevel)}
                    className={`px-4 py-2 rounded-md transition-colors ${
                      activeLevel === level
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
              <LessonAdminPanel activeLevel={activeLevel} language={adminLanguage} />
            </div>
          )}
          {activeTab === 'requests' && <CustomLessonRequests />}
          {activeTab === 'issues' && <IssueReports />}
          {activeTab === 'words' && <WordBank />}
          {activeTab === 'language' && <LanguageSelection onSelect={() => {}} />}
          {activeTab === 'level' && <LevelSelection onSelect={() => {}} />}
          {activeTab === 'account' && <AccountInfo user={{} as UserProfile} />}
          {activeTab === 'settings' && <Settings />}
        </div>
        {activeTab === 'admins' && <AdminUsers />}
        {activeTab === 'premium' && <PremiumUsers />}
        {activeTab === 'donations' && <DonationLinksManager />}
      </div>
    </div>
  );
}